import TranslationService from 'app/common/services/translation';
import DomUtils from 'app/common/utils/dom';
import log from 'app/common/utils/log';
import 'app/sass/styles.scss';
import 'focus-visible';
import { useFormik } from 'formik';
import * as React from 'react';
import { emailValidationSchema, passcodeValidationSchema } from '../constant';
import type { ValidateSecureSharingAccessError } from './api';
import api from './api';
import ChallengeSecureSharing from './challenge-secure-sharing';
import DeviceStickinessError from './device-stickiness-error';
import EmailValidationSuccess from './email-validation-success';
import SecureSharingHeader from './secure-sharing-header';
import SharingLinkValidationError from './sharing-link-validation-error';
import { ChallengeType, SharingAccessErrorCode } from './types';

const initialValues = { challengeInput: '' };

interface SecureFileSharingData {
  challenge: ChallengeType | null;
  linkGuid: string;
  errorCode: SharingAccessErrorCode;
}

function SecureSharingPage() {
  const {
    challenge: challengeType,
    linkGuid,
    errorCode,
  } = DomUtils.getScriptData<SecureFileSharingData>(
    '#secure-file-sharing-challenge-data',
  );
  const [sharingState, setSharingState] = React.useState<
    'submitting' | 'redirecting' | 'idle' | 'success'
  >('idle');
  const [sharingAcccessError, setSharingAcccessError] =
    React.useState<SharingAccessErrorCode>();

  const handleSubmit = async (challengeInput: string) => {
    if (!challengeType) {
      return;
    }

    const validateParams = {
      linkGuid,
      challengeType,
      challengeInput,
    };

    try {
      setSharingState('submitting');
      const data = await api.validateSecureSharingAccess(validateParams);

      if (!data) {
        if (challengeType === ChallengeType.Email) setSharingState('success');
        return;
      }

      if ('errorTitle' in data) {
        const errorTitle = getUserFacingErrorTitle(data);

        formik.setErrors({ challengeInput: errorTitle });
        setSharingAcccessError(data.errorCode);
        setSharingState('idle');
        return;
      }

      setSharingState('redirecting');
      window.location.replace(data.RedirectUrl);
    } catch (error) {
      if (error instanceof Error) {
        log.error(error);
      }
      setSharingState('idle');
    }
  };

  const validationSchema =
    challengeType === ChallengeType.Email
      ? emailValidationSchema
      : passcodeValidationSchema;

  const formik = useFormik({
    validationSchema,
    initialValues,
    onSubmit: ({ challengeInput }) => handleSubmit(challengeInput),
  });

  const handleChallengeInputChange = (challengeInput: string) => {
    formik.setValues({ challengeInput });
  };

  if (errorCode === 'wrongDevice') {
    return <DeviceStickinessError />;
  }

  if (errorCode || sharingAcccessError === 'notFound') {
    return <SharingLinkValidationError />;
  }

  if (!challengeType) return <></>;

  if (sharingState === 'success') {
    return <EmailValidationSuccess email={formik.values.challengeInput} />;
  }

  return (
    <ChallengeSecureSharing
      onSubmit={formik.handleSubmit}
      onChallengeInputChange={handleChallengeInputChange}
      challengeType={challengeType}
      buttonState={sharingState}
      errors={formik.errors.challengeInput}
    />
  );
}

const SecureSharingPageContainer = () => {
  return (
    <>
      <SecureSharingHeader />
      <SecureSharingPage />
    </>
  );
};

const getUserFacingErrorTitle = (
  errorResponse: ValidateSecureSharingAccessError,
) => {
  switch (errorResponse.errorCode) {
    case 'invalidPasscode':
      return TranslationService.get('PasscodeIsNotCorrect');

    default:
      return errorResponse.errorTitle;
  }
};

export default SecureSharingPageContainer;
